import React from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, SignIn, RedirectToSignIn, ClerkLoading, ClerkLoaded } from "@clerk/clerk-react";
import "@/App.css";
import { Toaster } from "sonner";
import { BrandProvider } from "@/lib/brand";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Agents from "@/pages/Agents";
import ChiefOfStaff from "@/pages/ChiefOfStaff";
import Concierge from "@/pages/Concierge";
import MeetingRoom from "@/pages/MeetingRoom";
import Approvals from "@/pages/Approvals";
import Payments from "@/pages/Payments";
import Tasks from "@/pages/Tasks";
import Automations from "@/pages/Automations";
import Uploads from "@/pages/Uploads";
import Documents from "@/pages/Documents";
import Media from "@/pages/Media";
import Downloads from "@/pages/Downloads";
import Integrations from "@/pages/Integrations";
import Notifications from "@/pages/Notifications";
import Audit from "@/pages/Audit";
import Brands from "@/pages/Brands";
import Ecosystem from "@/pages/Ecosystem";
import LeadInbox from "@/pages/LeadInbox";
import EmailTemplates from "@/pages/EmailTemplates";
import Blog from "@/pages/Blog";
import Gallery from "@/pages/Gallery";
import Testimonials from "@/pages/Testimonials";
import Newsletter from "@/pages/Newsletter";
import WebsiteSettings from "@/pages/WebsiteSettings";
import CmsCollection from "@/pages/CmsCollection";
import Growth from "@/pages/Growth";
import GrowthPortal from "@/pages/GrowthPortal";
import Social from "@/pages/Social";
import Settings from "@/pages/Settings";

const PUB_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const LOGO_URL = typeof window !== "undefined" ? `${window.location.origin}/ggg-logo.webp` : "/ggg-logo.webp";

const clerkAppearance = {
  layout: {
    logoPlacement: "none",
  },
  variables: {
    colorPrimary: "#d4af37",
    colorBackground: "transparent",
    colorInputBackground: "#0b0b0b",
    colorText: "#fafafa",
    colorTextSecondary: "#a1a1aa",
    colorInputText: "#fafafa",
    colorNeutral: "#fafafa",
    borderRadius: "0.6rem",
    fontFamily: '"IBM Plex Sans", sans-serif',
  },
  elements: {
    rootBox: "w-full",
    cardBox: "w-full max-w-full shadow-none",
    card: "bg-transparent shadow-none border-0 p-0 w-full max-w-full gap-6",
    header: "gap-1",
    headerTitle: "hidden",
    headerSubtitle: "text-white/60 text-sm text-center",
    logoBox: "hidden",
    main: "w-full gap-4",
    form: "w-full",
    formField: "w-full",
    formFieldLabelRow: "w-full mb-1",
    formFieldLabel: "text-white/80 text-sm",
    socialButtons: "w-full gap-2",
    socialButtonsBlockButton:
      "bg-white/95 hover:bg-white text-black border-0 shadow-[0_2px_8px_rgba(0,0,0,0.4)]",
    socialButtonsBlockButtonText: "text-black font-medium",
    socialButtonsIconButton:
      "bg-white/95 hover:bg-white border-0 shadow-[0_2px_8px_rgba(0,0,0,0.4)]",
    dividerRow: "w-full",
    dividerLine: "bg-white/10",
    dividerText: "text-white/40",
    formFieldRow: "w-full",
    formButtonPrimary:
      "w-full bg-gradient-to-b from-[#e7c65a] to-[#b98f24] text-black font-semibold hover:from-[#f0d574] hover:to-[#c99a28] shadow-[0_4px_16px_rgba(212,175,55,0.25)]",
    formFieldInput: "w-full bg-[#0b0b0b] border border-white/10 focus:border-[#d4af37]/50",
    footerActionLink: "text-[#d4af37] hover:text-[#e7c65a]",
    footer: "hidden",
  },
};

// Global appearance for ClerkProvider — MUST NOT include w-full element rules,
// otherwise the header <UserButton/> inherits them and eats the top bar's free space.
const clerkGlobalAppearance = {
  variables: {
    colorPrimary: "#d4af37",
    colorBackground: "#0b0b0b",
    colorInputBackground: "#0b0b0b",
    colorText: "#fafafa",
    colorTextSecondary: "#a1a1aa",
    colorInputText: "#fafafa",
    colorNeutral: "#fafafa",
    borderRadius: "0.6rem",
    fontFamily: '"IBM Plex Sans", sans-serif',
  },
  elements: {
    userButtonPopoverCard: "bg-[#0b0b0b] border border-white/10",
    userButtonTrigger: "shrink-0 focus:shadow-none rounded-full",
  },
};

function AuthLanding() {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 login-bg" data-testid="auth-landing">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-[#d4af37]/10 blur-[100px]" />
        <div className="flex items-center gap-3 relative">
          <img src="/ggg-logo.webp" alt="Gopi Gadhvi Group" className="h-16 w-auto" />
          <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">Command Centre</span>
        </div>
        <div className="max-w-md relative">
          <h1 className="font-head text-5xl font-black leading-[1.05] tracking-tight bg-gradient-to-b from-[#f4d789] via-[#d4a63c] to-[#a9781f] bg-clip-text text-transparent">
            The Gopi Gadhvi Group Command Centre
          </h1>
          <p className="text-white/55 mt-5 text-sm leading-relaxed">
            The central operating system sitting above Gopi Gadhvi Group, Monarch Estates,
            Monarch Design Studio, The Art Forest and The Wishing Well. AI orchestration,
            approvals, governance and intelligence — in one secure headquarters.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["GOPI GADHVI GROUP", "MONARCH ESTATES", "MONARCH DESIGN", "THE ART FOREST", "THE WISHING WELL"].map((b) => (
              <span key={b} className="text-[10px] font-mono uppercase tracking-wider border border-[#d4af37]/20 rounded-full px-2.5 py-1 text-white/45">
                {b}
              </span>
            ))}
          </div>
        </div>
        <div className="text-[11px] font-mono text-white/35 relative">SECURE · CLERK AUTHENTICATION · MESHAPI</div>
      </div>
      <div className="flex flex-col items-center justify-center p-6 sm:p-10 w-full">
        <div className="neu-login login-card rounded-3xl p-8 md:p-10 w-full max-w-md fade-up flex flex-col justify-center" data-testid="login-card">
          <div className="flex flex-col items-center mb-7 shrink-0">
            <img src="/ggg-logo.webp" alt="Gopi Gadhvi Group" className="h-16 w-auto mb-3" />
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-primary">Command Centre</div>
            <h2 className="font-head text-xl font-semibold text-white/90 mt-2">Welcome back</h2>
            <p className="text-white/45 text-xs mt-1">Sign in to your secure headquarters</p>
          </div>
          <ClerkLoading>
            <div className="flex flex-col items-center justify-center gap-4 min-h-[300px]">
              <img src="/ggg-logo.webp" alt="Gopi Gadhvi Group" className="h-12 w-auto animate-pulse" />
              <div className="text-white/50 text-sm font-mono">Loading secure sign-in…</div>
            </div>
          </ClerkLoading>
          <ClerkLoaded>
            <div className="min-h-[300px] flex flex-col justify-center">
              <SignIn routing="hash" fallbackRedirectUrl="/" appearance={clerkAppearance} />
            </div>
          </ClerkLoaded>
        </div>
      </div>
    </div>
  );
}

function ClerkWithRouter({ children }) {
  const navigate = useNavigate();
  if (!PUB_KEY) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="border border-warn/40 bg-warn/5 rounded-md p-6 max-w-md">
          <div className="text-warn font-mono text-xs uppercase tracking-wider mb-2">Configuration Required</div>
          <p className="text-sm text-muted-foreground">Clerk publishable key is missing. Set REACT_APP_CLERK_PUBLISHABLE_KEY.</p>
        </div>
      </div>
    );
  }
  return (
    <ClerkProvider
      publishableKey={PUB_KEY}
      appearance={clerkGlobalAppearance}
      signInUrl="/sign-in"
      signInFallbackRedirectUrl="/"
      afterSignOutUrl="/sign-in"
    >
      {children}
    </ClerkProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <ClerkWithRouter>
        <BrandProvider>
          <Toaster theme="dark" position="bottom-right" richColors closeButton />
          <Routes>
            <Route path="/sign-in/*" element={<AuthLanding />} />
            <Route
              path="/*"
              element={
                <>
                  <SignedIn>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/chief" element={<ChiefOfStaff />} />
                        <Route path="/concierge" element={<Concierge />} />
                        <Route path="/meeting" element={<MeetingRoom />} />
                        <Route path="/agents" element={<Agents />} />
                        <Route path="/approvals" element={<Approvals />} />
                        <Route path="/payments" element={<Payments />} />
                        <Route path="/tasks" element={<Tasks />} />
                        <Route path="/automations" element={<Automations />} />
                        <Route path="/leads" element={<LeadInbox />} />
                        <Route path="/email-templates" element={<EmailTemplates />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/gallery" element={<Gallery />} />
                        <Route path="/testimonials" element={<Testimonials />} />
                        <Route path="/newsletter" element={<Newsletter />} />
                        <Route path="/faq" element={<CmsCollection kind="faq" />} />
                        <Route path="/team" element={<CmsCollection kind="team" />} />
                        <Route path="/services" element={<CmsCollection kind="services" />} />
                        <Route path="/website-settings" element={<WebsiteSettings />} />
                        <Route path="/growth" element={<Growth />} />
                        <Route path="/growth/:brand" element={<GrowthPortal />} />
                        <Route path="/social" element={<Social />} />
                        <Route path="/uploads" element={<Uploads />} />
                        <Route path="/documents" element={<Documents />} />
                        <Route path="/media" element={<Media />} />
                        <Route path="/downloads" element={<Downloads />} />
                        <Route path="/integrations" element={<Integrations />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/audit" element={<Audit />} />
                        <Route path="/brands" element={<Brands />} />
                        <Route path="/ecosystem" element={<Ecosystem />} />
                        <Route path="/settings" element={<Settings />} />
                      </Routes>
                    </Layout>
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
          </Routes>
        </BrandProvider>
      </ClerkWithRouter>
    </div>
  );
}
